import React from "react";
import { Container, Typography, Box, Grid, Paper, Divider } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(6),
  },
  link: {
    color: "#ffeb3b",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "none",
      opacity: 0.8,
    },
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },
  tokenomicsGrid: {
    marginTop: theme.spacing(2),
  },
  tokenomicsItem: {
    padding: theme.spacing(2),
    textAlign: "center",
    backgroundColor: "rgba(255, 235, 59, 0.1)",
    borderRadius: theme.spacing(1),
    border: "1px solid rgba(255, 235, 59, 0.3)",
  },
  roadmapItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: theme.spacing(1),
    borderLeft: "4px solid #ffeb3b",
  },
  securityFeature: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "& .MuiSvgIcon-root": {
      color: "#4caf50",
      marginRight: theme.spacing(1),
    },
  },
}));

const Whitepaper = () => {
  const classes = useStyles();

  return (
    <Container maxWidth="lg">
      <Typography variant="h2" gutterBottom align="center" sx={{ mb: 4 }}>
        Retro Llama Token (LLAMA)
      </Typography>
      <Typography variant="h4" gutterBottom align="center" color="textSecondary" sx={{ mb: 6 }}>
        Technical Whitepaper v2.0
      </Typography>

      {/* Executive Summary */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Executive Summary
        </Typography>
        <Typography variant="body1" paragraph>
          Retro Llama Token (LLAMA) is a community-driven, deflationary cryptocurrency built on the Solana blockchain, 
          designed to combine the nostalgic appeal of 80s culture with cutting-edge DeFi technology. Our mission is to 
          create a sustainable, transparent, and engaging ecosystem that rewards long-term holders while fostering 
          community growth and innovation.
        </Typography>
        <Typography variant="body1" paragraph>
          With a focus on security, transparency, and community governance, LLAMA aims to establish itself as a 
          leading memecoin in the Solana ecosystem, offering real utility through staking rewards, NFT integration, 
          and community-driven development initiatives.
        </Typography>
      </Paper>

      {/* Project Overview */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Project Overview
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Project Name:</strong> Retro Llama Token (LLAMA)
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Blockchain:</strong> Solana (SOL)
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Token Standard:</strong> SPL Token (Solana Program Library)
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Contract Address:</strong> 5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Explorer:</strong>{" "}
          <a 
            href="https://explorer.solana.com/address/5ybhspwr2nPH4zTXq95QdE4CuoxmasDuJ1d1h74hmehb" 
            target="_blank" 
            rel="noopener noreferrer"
            className={classes.link}
          >
            Solana Explorer
          </a>
        </Typography>
      </Paper>

      {/* Enhanced Tokenomics */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Tokenomics & Economic Model
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Supply Distribution
        </Typography>
        <Grid container spacing={3} className={classes.tokenomicsGrid}>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.tokenomicsItem}>
              <Typography variant="h6" color="primary">30%</Typography>
              <Typography variant="body2">Liquidity Pool</Typography>
              <Typography variant="caption" color="textSecondary">
                Locked for 1 year minimum
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.tokenomicsItem}>
              <Typography variant="h6" color="primary">25%</Typography>
              <Typography variant="body2">Community Rewards</Typography>
              <Typography variant="caption" color="textSecondary">
                Staking, airdrops, contests
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.tokenomicsItem}>
              <Typography variant="h6" color="primary">15%</Typography>
              <Typography variant="body2">Development Fund</Typography>
              <Typography variant="caption" color="textSecondary">
                Team & technical development
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.tokenomicsItem}>
              <Typography variant="h6" color="primary">15%</Typography>
              <Typography variant="body2">Marketing & Partnerships</Typography>
              <Typography variant="caption" color="textSecondary">
                Growth initiatives & collaborations
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.tokenomicsItem}>
              <Typography variant="h6" color="primary">10%</Typography>
              <Typography variant="body2">Reserve Fund</Typography>
              <Typography variant="caption" color="textSecondary">
                Emergency & strategic reserves
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.tokenomicsItem}>
              <Typography variant="h6" color="primary">5%</Typography>
              <Typography variant="body2">Advisory & Legal</Typography>
              <Typography variant="caption" color="textSecondary">
                Professional services
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" gutterBottom>
          Economic Features
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Total Supply:</strong> 1,000,000,000 LLAMA (Fixed)
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Deflationary Mechanism:</strong> 2% burn on all transactions
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Staking Rewards:</strong> Up to 15% APY for long-term holders
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Liquidity Mining:</strong> Earn LLAMA by providing liquidity
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Anti-Dump Protection:</strong> Gradual vesting for team tokens
        </Typography>
      </Paper>

      {/* Security & Transparency */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Security & Transparency
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Smart Contract Security
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Box className={classes.securityFeature}>
            <Typography variant="body1">✅ Verified Contract on Solana Explorer</Typography>
          </Box>
          <Box className={classes.securityFeature}>
            <Typography variant="body1">✅ Liquidity Locked for 1 Year Minimum</Typography>
          </Box>
          <Box className={classes.securityFeature}>
            <Typography variant="body1">✅ No Mint Function - Fixed Supply</Typography>
          </Box>
          <Box className={classes.securityFeature}>
            <Typography variant="body1">✅ Multi-Signature Wallet Protection</Typography>
          </Box>
          <Box className={classes.securityFeature}>
            <Typography variant="body1">✅ Renounced Ownership (Post-Launch)</Typography>
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          Transparency Measures
        </Typography>
        <Typography variant="body1" paragraph>
          • Real-time liquidity monitoring and public wallet tracking
        </Typography>
        <Typography variant="body1" paragraph>
          • Monthly transparency reports and community updates
        </Typography>
        <Typography variant="body1" paragraph>
          • Public team wallet addresses with transaction history
        </Typography>
        <Typography variant="body1" paragraph>
          • Community governance for major decisions
        </Typography>
      </Paper>

      {/* Technology & Infrastructure */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Technology & Infrastructure
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Solana Blockchain Benefits
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>High Performance:</strong> 65,000+ TPS with sub-second finality
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Low Fees:</strong> Average transaction cost under $0.01
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Scalability:</strong> Horizontal scaling through validator network
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Eco-Friendly:</strong> Proof of Stake consensus mechanism
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Technical Architecture
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Frontend:</strong> React.js with Material-UI components
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Wallet Integration:</strong> Phantom, Solflare, and other Solana wallets
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>DEX Integration:</strong> Raydium, Jupiter, Orca for trading
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Analytics:</strong> Real-time price feeds and market data
        </Typography>
      </Paper>

      {/* Use Cases & Utility */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Use Cases & Utility
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Primary Use Cases
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Staking Rewards:</strong> Earn passive income by staking LLAMA tokens
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Governance:</strong> Participate in community voting and decision-making
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>NFT Marketplace:</strong> Trade retro-themed NFTs with LLAMA
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Liquidity Mining:</strong> Provide liquidity and earn rewards
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Community Events:</strong> Access exclusive events and airdrops
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Future Utility
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Gaming Integration:</strong> Retro-themed blockchain games
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Merchandise Store:</strong> Purchase official merchandise with LLAMA
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>Cross-Chain Bridge:</strong> Multi-chain compatibility
        </Typography>
        <Typography variant="body1" paragraph>
          • <strong>DeFi Protocols:</strong> Integration with lending and yield farming
        </Typography>
      </Paper>

      {/* Enhanced Roadmap */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Development Roadmap
        </Typography>
        
        <Box className={classes.roadmapItem}>
          <Typography variant="h6" color="primary">Phase 1: Foundation (Q4 2024)</Typography>
          <Typography variant="body1" paragraph>
            • Smart contract development and security audit
          </Typography>
          <Typography variant="body1" paragraph>
            • Initial liquidity provision and token launch
          </Typography>
          <Typography variant="body1" paragraph>
            • Community building and social media presence
          </Typography>
          <Typography variant="body1" paragraph>
            • Website and trading platform development
          </Typography>
        </Box>

        <Box className={classes.roadmapItem}>
          <Typography variant="h6" color="primary">Phase 2: Growth (Q1 2025)</Typography>
          <Typography variant="body1" paragraph>
            • Staking platform launch with reward mechanisms
          </Typography>
          <Typography variant="body1" paragraph>
            • Major exchange listings and DEX integrations
          </Typography>
          <Typography variant="body1" paragraph>
            • Marketing campaigns and influencer partnerships
          </Typography>
          <Typography variant="body1" paragraph>
            • Community governance system implementation
          </Typography>
        </Box>

        <Box className={classes.roadmapItem}>
          <Typography variant="h6" color="primary">Phase 3: Expansion (Q2 2025)</Typography>
          <Typography variant="body1" paragraph>
            • NFT marketplace and retro-themed collections
          </Typography>
          <Typography variant="body1" paragraph>
            • Mobile app development for iOS and Android
          </Typography>
          <Typography variant="body1" paragraph>
            • Cross-chain bridge implementation
          </Typography>
          <Typography variant="body1" paragraph>
            • Advanced DeFi integrations and yield farming
          </Typography>
        </Box>

        <Box className={classes.roadmapItem}>
          <Typography variant="h6" color="primary">Phase 4: Ecosystem (Q3-Q4 2025)</Typography>
          <Typography variant="body1" paragraph>
            • Gaming platform and retro-themed games
          </Typography>
          <Typography variant="body1" paragraph>
            • Merchandise store and real-world partnerships
          </Typography>
          <Typography variant="body1" paragraph>
            • DAO governance and community-driven development
          </Typography>
          <Typography variant="body1" paragraph>
            • Global expansion and international markets
          </Typography>
        </Box>
      </Paper>

      {/* Risk Factors */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Risk Factors & Disclaimers
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Market Risks:</strong> Cryptocurrency markets are highly volatile and unpredictable. 
          Token prices can fluctuate significantly and may result in substantial losses.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Regulatory Risks:</strong> Changes in cryptocurrency regulations may impact the project's 
          operations and token value.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Technical Risks:</strong> Smart contract vulnerabilities, blockchain network issues, 
          and technological failures could affect the project.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Competition:</strong> The cryptocurrency market is highly competitive with many 
          established and emerging projects.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Disclaimer:</strong> This whitepaper is for informational purposes only and does not 
          constitute financial advice. Always conduct your own research before investing.
        </Typography>
      </Paper>

      {/* Team & Advisors */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Team & Advisors
        </Typography>
        <Typography variant="body1" paragraph>
          Our team consists of experienced blockchain developers, marketing professionals, and 
          community managers with backgrounds in cryptocurrency, DeFi, and community building.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Core Team:</strong> Anonymous team with proven track records in successful 
          cryptocurrency projects and community management.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Advisors:</strong> Industry experts in blockchain technology, DeFi protocols, 
          and cryptocurrency marketing.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Community:</strong> Our greatest asset - a passionate and engaged community 
          driving the project's success.
        </Typography>
      </Paper>

      {/* Conclusion */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Conclusion
        </Typography>
        <Typography variant="body1" paragraph>
          Retro Llama Token represents a unique opportunity to participate in a community-driven 
          cryptocurrency that combines nostalgic appeal with modern blockchain technology. With a 
          focus on security, transparency, and sustainable growth, LLAMA is positioned to become 
          a leading memecoin in the Solana ecosystem.
        </Typography>
        <Typography variant="body1" paragraph>
          Our commitment to continuous development, community engagement, and long-term value 
          creation sets us apart from typical memecoins. We invite you to join our community 
          and be part of this exciting journey.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Join the Retro Revolution!</strong>
        </Typography>
      </Paper>

      {/* Contact Information */}
      <Paper className={classes.paper}>
        <Typography variant="h4" gutterBottom>
          Contact & Community
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Website:</strong>{" "}
          <a href="https://retrollama.com" target="_blank" rel="noopener noreferrer" className={classes.link}>
            https://retrollama.com
          </a>
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Telegram:</strong>{" "}
          <a href="https://t.me/RetroLlama" target="_blank" rel="noopener noreferrer" className={classes.link}>
            @RetroLlama
          </a>
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Twitter:</strong>{" "}
          <a href="https://twitter.com/RetroLlamaToken" target="_blank" rel="noopener noreferrer" className={classes.link}>
            @RetroLlamaToken
          </a>
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Discord:</strong>{" "}
          <a href="https://discord.gg/retrollama" target="_blank" rel="noopener noreferrer" className={classes.link}>
            Join our Discord
          </a>
        </Typography>
      </Paper>
    </Container>
  );
};

export default Whitepaper;
